<template>
  <aside class="advanced-panel" :class="{ minimized }" :style="panelStyle">
    <header class="advanced-header" @pointerdown="startDrag">
      <strong>Filtros avanzados</strong>
      <div>
        <button type="button" @pointerdown.stop @click="$emit('toggle-minimized')">{{ minimized ? '+' : '−' }}</button>
        <button type="button" @pointerdown.stop @click="closePanel">×</button>
      </div>
    </header>

    <div v-show="!minimized" class="advanced-body">
      <section class="advanced-section">
        <div class="section-title">
          <label><input type="checkbox" :checked="draft.attribute.enabled" @change="toggleSection('attribute', $event.target.checked)"> Filtro por atributo</label>
        </div>
        <AdvancedAttributeGroup
          v-if="draft.attribute.enabled"
          :group="draft.attribute.root"
          :schema="schema"
          :fields="fields"
          :aliases="aliases"
          :distinct-values-fetcher="sourceDistinctValuesFetcher"
          @change="updateRoot"
          @apply="applyDraft"
        />
      </section>

      <section class="advanced-section">
        <div class="section-title">
          <label><input type="checkbox" :checked="draft.roi.enabled" @change="toggleSection('roi', $event.target.checked)"> Región de interés</label>
        </div>
        <template v-if="draft.roi.enabled">
          <label class="control-label">Relación espacial
            <select :value="roiOperation" @change="changeOperation($event.target.value)">
              <option value="intersects">Intersecta</option>
              <option value="featureWithinArea">La entidad está dentro del área</option>
              <option value="featureContainsArea">La entidad contiene el área</option>
            </select>
          </label>
          <div class="shape-grid">
            <button type="button" :class="{ active: activeShapeKind === 'viewport' }" @click="useViewport">Vista actual</button>
            <button type="button" :class="{ active: activeShapeKind === 'rectangle' }" @click="startDrawing('rectangle')">Rectángulo</button>
            <button type="button" :class="{ active: activeShapeKind === 'circle' }" @click="startDrawing('circle')">Círculo</button>
            <button type="button" :class="{ active: activeShapeKind === 'polygon' }" @click="startDrawing('polygon')">Polígono</button>
          </div>

          <div v-if="roiSpec" class="geometry-summary">
            <p class="shape-caption">{{ shapeLabel }}</p>
            <button type="button" class="edit-coordinates" :class="{ active: coordinateEditorOpen }" @click="coordinateEditorOpen = !coordinateEditorOpen">
              <font-awesome-icon icon="pencil-alt" /> {{ coordinateEditorOpen ? 'Ocultar edición' : 'Editar coordenadas' }}
            </button>
          </div>
          <div v-if="roiSpec && coordinateEditorOpen" class="numeric-editor">
            <template v-if="isRectangle">
              <div class="numeric-grid four">
                <label v-for="(label, index) in bboxLabels" :key="label">{{ label }}
                  <input type="number" step="0.000001" :value="formatNumber(roiSpec.shape.bbox[index])" @change="updateBbox(index, $event.target.value)">
                </label>
              </div>
              <div class="numeric-grid">
                <label>Centro longitud<input type="number" step="0.000001" :value="formatNumber(rectCenter[0])" @change="updateRectCenter(0, $event.target.value)"></label>
                <label>Centro latitud<input type="number" step="0.000001" :value="formatNumber(rectCenter[1])" @change="updateRectCenter(1, $event.target.value)"></label>
                <label>Ancho (m)<input type="number" min="0" step="1" :value="formatNumber(rectDimensions[0], 2)" @change="updateRectDimension(0, $event.target.value)"></label>
                <label>Alto (m)<input type="number" min="0" step="1" :value="formatNumber(rectDimensions[1], 2)" @change="updateRectDimension(1, $event.target.value)"></label>
              </div>
            </template>
            <div v-else-if="roiSpec.shape.kind === 'circle'" class="numeric-grid">
              <label>Centro longitud<input type="number" step="0.000001" :value="formatNumber(roiSpec.shape.center[0])" @change="updateCircleCenter(0, $event.target.value)"></label>
              <label>Centro latitud<input type="number" step="0.000001" :value="formatNumber(roiSpec.shape.center[1])" @change="updateCircleCenter(1, $event.target.value)"></label>
              <label>Radio (m)<input type="number" min="0" step="1" :value="formatNumber(roiSpec.shape.radiusMeters, 2)" @change="updateRadius($event.target.value)"></label>
            </div>
            <div v-else class="vertices-editor">
              <div v-for="(vertex, index) in roiSpec.shape.vertices" :key="index" class="vertex-row">
                <span>V{{ index + 1 }}</span>
                <input type="number" step="0.000001" :value="formatNumber(vertex[0])" @change="updateVertex(index, 0, $event.target.value)">
                <input type="number" step="0.000001" :value="formatNumber(vertex[1])" @change="updateVertex(index, 1, $event.target.value)">
              </div>
              <p>Área: {{ polygonMetrics.area }} · Perímetro: {{ polygonMetrics.perimeter }}</p>
            </div>
          </div>
        </template>
      </section>

      <section class="advanced-section">
        <div class="section-title cross-layer-title">
          <label><input type="checkbox" :checked="draft.crossLayer.enabled" @change="toggleSection('crossLayer', $event.target.checked)"> Filtro por capa</label>
          <button v-if="draft.crossLayer.enabled && draft.crossLayer.targetLayerId && targetGeometryFamily === 'Polygon'"
            type="button" class="click-select-icon-btn" :class="{ active: targetSelectionMode === 'click' }"
            :disabled="!targetLayerActive"
            :title="!targetLayerActive ? 'Active la capa objetivo en el mapa para poder seleccionar entidades' : (targetSelectionMode === 'click' ? 'Volver a filtro por atributos' : 'Seleccionar entidades clickeando en el mapa')"
            @click="toggleTargetSelectionMode">
            <font-awesome-icon icon="crosshairs" />
          </button>
        </div>
        <template v-if="draft.crossLayer.enabled">
          <label class="control-label">Capa objetivo
            <select :value="draft.crossLayer.targetLayerId || ''" @change="changeTarget($event.target.value)">
              <option value="" disabled>Seleccione una capa activa</option>
              <option v-for="option in targetOptions" :key="option.id" :value="option.id">{{ option.name }}</option>
            </select>
          </label>
          <label v-if="draft.crossLayer.targetLayerId" class="control-label cross-operation">Relación de la capa principal
            <select :value="draft.crossLayer.operation" @change="changeCrossOperation($event.target.value)">
              <option value="intersects">Intersecta la capa objetivo</option>
              <option value="sourceWithinTarget">Está contenida en la capa objetivo</option>
              <option value="sourceContainsTarget">Contiene la capa objetivo</option>
            </select>
          </label>
          <label v-if="draft.crossLayer.targetLayerId" class="cross-roi-toggle">
            <input type="checkbox" :checked="draft.crossLayer.applyRoiToTarget" :disabled="!roiSpec || targetSelectionMode === 'click'" @change="toggleTargetRoi($event.target.checked)">
            Aplicar también la región de interés a la capa objetivo
          </label>
          <template v-if="targetSelectionMode === 'attribute'">
            <p v-if="targetComplexity && targetComplexity.complex" class="cross-status hint">Esta capa objetivo tiene geometría compleja ({{ targetComplexity.coordinateCount.toLocaleString('es-CL') }} coordenadas) — aplicar el filtro puede tardar hasta un minuto, se puede cancelar mientras se resuelve.</p>
            <p v-else-if="draft.crossLayer.targetLayerId && !targetComplexity" class="cross-status hint">Si la capa objetivo tiene geometría compleja (muchas islas o partes), aplicar el filtro puede tardar — se puede cancelar mientras se resuelve.</p>
          </template>
          <p v-if="targetLoading" class="cross-status">Consultando atributos de la capa objetivo…</p>
          <p v-else-if="targetError" class="cross-status error">{{ targetError }}</p>
          <div v-else-if="draft.crossLayer.targetLayerId && targetSelectionMode === 'attribute'" class="target-attribute-filter">
            <p>Condiciones opcionales de la capa objetivo</p>
            <AdvancedAttributeGroup
              :group="draft.crossLayer.targetAttribute.root"
              :schema="targetSchema"
              :fields="targetFields"
              :aliases="targetAliases"
              :distinct-values-fetcher="targetDistinctValuesFetcher"
              @change="updateTargetRoot"
              @apply="applyDraft"
            />
          </div>
          <div v-else-if="draft.crossLayer.targetLayerId && targetSelectionMode === 'click'" class="target-click-selection">
            <p v-if="!targetLayerActive" class="cross-status hint">Active la capa objetivo en el mapa para poder seleccionar entidades.</p>
            <template v-else>
              <p class="cross-status hint"><font-awesome-icon icon="crosshairs" /> Clic en el mapa para agregar una entidad; un segundo clic sobre la misma la quita.</p>
              <p class="selection-count">{{ targetSelectionIds.length }} entidad{{ targetSelectionIds.length === 1 ? '' : 'es' }} seleccionada{{ targetSelectionIds.length === 1 ? '' : 's' }}</p>
              <ul v-if="targetSelectionIds.length" class="selection-chips">
                <li v-for="(id, index) in targetSelectionIds" :key="id" class="chip">
                  Entidad {{ index + 1 }}
                  <button type="button" title="Quitar" aria-label="Quitar entidad" @click="removeSelectedFeature(id)"><font-awesome-icon icon="trash-alt" /></button>
                </li>
              </ul>
              <button v-if="targetSelectionIds.length" type="button" class="small" @click="clearMapSelection">Limpiar selección</button>
            </template>
          </div>
        </template>
      </section>

      <div v-if="errors.length" class="validation-errors">
        <p v-for="error in errors" :key="error">{{ error }}</p>
      </div>
    </div>

    <footer v-show="!minimized" class="advanced-footer">
      <button type="button" class="secondary" :disabled="busy" @click="$emit('clear')">Limpiar</button>
      <button type="button" class="secondary" :disabled="busy || !canApply" @click="saveDraft">Guardar…</button>
      <button type="button" class="primary" :disabled="busy || !canApply" @click="applyDraft">
        {{ busy ? 'Filtrando…' : 'Filtrar' }}
      </button>
    </footer>
  </aside>
</template>

<script>
import { getArea, getLength } from "ol/sphere";
import * as turf from "@turf/turf";
import AdvancedAttributeGroup from "./AdvancedAttributeGroup.vue";
import { advancedRoiService } from "@/services/map/advancedRoiService";
import { targetFeatureSelectionService, resolveInitialSelectionFeatures } from "@/services/map/targetFeatureSelectionService";
import { cloneFilter, hasAttributePredicate, newGroup, roiPolygon4326, validateAttributeTree } from "@/services/layers/advancedFilters";
import { attributesTableService } from "@/services/layers/attributesTable";
import { makeDistinctValuesFetcher } from "@/services/layers/distinctValues";
import { mapService } from "@/services/map/mapService";
import Polygon from "ol/geom/Polygon";
import { transform } from "ol/proj";

export default {
  name: "AdvancedFilterPanel",
  components: { AdvancedAttributeGroup },
  props: {
    draft: { type: Object, required: true },
    schema: { type: Object, default: () => ({}) },
    fields: { type: Array, default: () => [] },
    aliases: { type: Object, default: () => ({}) },
    sourceLayer: { type: Object, required: true },
    minimized: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    bottomOffset: { type: Number, default: 280 },
  },
  data() {
    // Se abre pegado al borde izquierdo, pero corrido lo suficiente para no
    // tapar la botonera de accesos directos del mapa (MapControlsCustom.vue
    // ::.map-controls, left:36px + 28px de ancho de botón ≈ 64px) — un
    // x:16 fijo quedaba justo encima de esos botones.
    const leftInset = 80;
    return {
      x: leftInset, y: 64, dragging: false, dragStart: null, coordinateEditorOpen: false,
      drawingKind: null,
      bboxLabels: ["Oeste", "Sur", "Este", "Norte"], targetSchema: {}, targetAliases: {}, targetLoading: false, targetError: "",
      targetComplexity: null, targetGeometryFamily: null,
    };
  },
  computed: {
    // AdvancedAttributeGroup se renderiza dos veces acá (capa fuente y,
    // si el filtro cruzado está activo, capa objetivo) — son capas
    // potencialmente distintas, así que cada una necesita su propio fetcher
    // ya atado a SU capa (ver services/layers/distinctValues.js).
    sourceDistinctValuesFetcher() { return makeDistinctValuesFetcher(this.sourceLayer); },
    targetDistinctValuesFetcher() {
      const id = this.draft.crossLayer.targetLayerId;
      return id ? makeDistinctValuesFetcher(attributesTableService.findKnownLayer(id)) : null;
    },
    roiSpec() { return this.draft.roi.spec; },
    roiOperation() { return this.roiSpec ? this.roiSpec.operation : "intersects"; },
    // Qué botón de figura resaltar: mientras se está dibujando (drawingKind,
    // seteado al hacer clic, antes de que termine el trazo en el mapa) tiene
    // prioridad; si no hay trazo en curso, refleja la figura ya definida.
    activeShapeKind() { return this.drawingKind || (this.roiSpec ? this.roiSpec.shape.kind : null); },
    targetFields() { return Object.keys(this.targetSchema).filter(field => this.targetSchema[field] !== "geometry"); },
    targetSelectionMode() { return this.draft.crossLayer.targetSelectionMode || "attribute"; },
    targetSelectionIds() { return (this.draft.crossLayer.targetSelection && this.draft.crossLayer.targetSelection.ids) || []; },
    targetLayerActive() {
      const id = this.draft.crossLayer.targetLayerId;
      return !!id && (this.$store.getters.getActiveLayers || []).some(layer => String(layer.id) === String(id));
    },
    targetOptions() {
      const active = (this.$store.getters.getActiveLayers || []).filter(layer => {
        if (!layer || String(layer.id) === String(this.sourceLayer.id)) return false;
        const url = String((layer.provider && layer.provider.url) || "");
        return !/\/ImageServer(?:\/\d+)?$/i.test(url) && layer.layerType !== "raster";
      });
      const saved = this.draft.crossLayer.targetLayerId && attributesTableService.findKnownLayer(this.draft.crossLayer.targetLayerId);
      if (saved && !active.some(layer => String(layer.id) === String(saved.id))) active.unshift(saved);
      return active;
    },
    isRectangle() { return this.roiSpec && ["rectangle", "viewport"].includes(this.roiSpec.shape.kind); },
    shapeLabel() { return ({ rectangle: "Rectángulo", viewport: "Vista actual", circle: "Círculo", polygon: "Polígono" })[this.roiSpec.shape.kind]; },
    rectCenter() {
      if (!this.isRectangle) return [0, 0]; const bbox = this.roiSpec.shape.bbox;
      return [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2];
    },
    rectDimensions() {
      if (!this.isRectangle) return [0, 0]; const bbox = this.roiSpec.shape.bbox; const center = this.rectCenter;
      return [this.distance([bbox[0], center[1]], [bbox[2], center[1]]), this.distance([center[0], bbox[1]], [center[0], bbox[3]])];
    },
    polygonMetrics() {
      if (!this.roiSpec || this.roiSpec.shape.kind !== "polygon") return { area: "0 m²", perimeter: "0 m" };
      const ring = roiPolygon4326(this.roiSpec)[0].map(point => transform(point, "EPSG:4326", "EPSG:3857"));
      const polygon = new Polygon([ring]);
      return { area: this.formatMetric(getArea(polygon), "m²"), perimeter: this.formatMetric(getLength(polygon), "m") };
    },
    errors() {
      const errors = [];
      if (this.draft.attribute.enabled && hasAttributePredicate(this.draft.attribute.root)) errors.push(...validateAttributeTree(this.draft.attribute.root, this.schema));
      if (this.draft.roi.enabled && this.roiSpec) errors.push(...this.validateRoi(this.roiSpec));
      if (this.draft.crossLayer.enabled) {
        if (!this.draft.crossLayer.targetLayerId) errors.push("Seleccione una capa objetivo");
        if (String(this.draft.crossLayer.targetLayerId) === String(this.sourceLayer.id)) errors.push("La capa objetivo debe ser distinta de la capa principal");
        if (this.targetError) errors.push(this.targetError);
        if (this.targetSelectionMode === "click") {
          if (!this.targetSelectionIds.length) errors.push("Seleccione al menos una entidad de la capa objetivo en el mapa");
        } else if (hasAttributePredicate(this.draft.crossLayer.targetAttribute.root)) {
          errors.push(...validateAttributeTree(this.draft.crossLayer.targetAttribute.root, this.targetSchema));
        }
      }
      return Array.from(new Set(errors));
    },
    canApply() {
      const attribute = this.draft.attribute.enabled && hasAttributePredicate(this.draft.attribute.root);
      const roi = this.draft.roi.enabled && !!this.roiSpec;
      const crossLayer = this.draft.crossLayer.enabled && !!this.draft.crossLayer.targetLayerId;
      return (attribute || roi || crossLayer) && !this.errors.length;
    },
    panelStyle() {
      return { left: `${this.x}px`, top: `${this.y}px`, maxHeight: `calc(100vh - ${Math.max(this.bottomOffset, 0) + this.y + 8}px)` };
    },
  },
  watch: {
    roiSpec: { deep: true, handler(spec) { if (spec) advancedRoiService.setSpec(spec, true); else advancedRoiService.clear(); } },
    "draft.crossLayer.targetLayerId": { immediate: true, handler(id) { this.loadTargetMetadata(id); } },
    targetSelectionMode() { this.syncClickSelectionMode(); },
    targetLayerActive() { this.syncClickSelectionMode(); },
    "draft.crossLayer.enabled"() { this.syncClickSelectionMode(); },
  },
  mounted() {
    advancedRoiService.init(mapService.get());
    advancedRoiService.setChangeHandler(spec => this.setRoi(spec));
    if (this.roiSpec) advancedRoiService.setSpec(this.roiSpec, true);
    targetFeatureSelectionService.init(mapService.get());
    this.syncClickSelectionMode();
  },
  beforeDestroy() {
    window.removeEventListener("pointermove", this.onDrag);
    window.removeEventListener("pointerup", this.stopDrag);
    advancedRoiService.setChangeHandler(null);
    advancedRoiService.stopInteractions();
    targetFeatureSelectionService.stop();
  },
  methods: {
    cloneDraft() { return cloneFilter(this.draft); },
    applyDraft() { if (!this.busy && this.canApply) this.$emit("apply", this.cloneDraft()); },
    saveDraft() { if (!this.busy && this.canApply) this.$emit("save", this.cloneDraft()); },
    emitDraft(next) { this.$emit("change", next); },
    updateRoot(root) { const next = this.cloneDraft(); next.attribute.root = root; this.emitDraft(next); },
    updateTargetRoot(root) { const next = this.cloneDraft(); next.crossLayer.targetAttribute.root = root; this.emitDraft(next); },
    changeTarget(id) {
      const layer = attributesTableService.findKnownLayer(id); const next = this.cloneDraft();
      next.crossLayer.targetLayerId = layer ? layer.id : null;
      next.crossLayer.targetLayerLabel = layer ? layer.name : "";
      next.crossLayer.targetAttribute = { enabled: true, root: newGroup(1) };
      next.crossLayer.targetSelectionMode = "attribute";
      next.crossLayer.targetSelection = { ids: [] };
      targetFeatureSelectionService.clear();
      this.emitDraft(next);
    },
    changeCrossOperation(operation) { const next = this.cloneDraft(); next.crossLayer.operation = operation; this.emitDraft(next); },
    toggleTargetRoi(enabled) { const next = this.cloneDraft(); next.crossLayer.applyRoiToTarget = enabled; this.emitDraft(next); },
    async loadTargetMetadata(id) {
      this.targetSchema = {}; this.targetAliases = {}; this.targetError = ""; this.targetGeometryFamily = null;
      // estimateCrossLayerTargetComplexity es síncrono (capas importadas ya
      // están en memoria) — no hace falta esperar al fetch de metadata para
      // saber si conviene mostrar la advertencia de "puede demorar".
      this.targetComplexity = id ? attributesTableService.estimateCrossLayerTargetComplexity(id) : null;
      if (!id) return;
      const layer = attributesTableService.findKnownLayer(id);
      if (!layer) { this.targetError = "La capa objetivo ya no está disponible"; return; }
      this.targetLoading = true;
      try {
        const metadata = await attributesTableService.getLayerMetadata(layer);
        this.targetSchema = metadata.schema || {}; this.targetAliases = metadata.aliases || {};
        this.targetGeometryFamily = metadata.geometryFamily || null;
      } catch (error) { this.targetError = error.message || "No se pudo consultar la capa objetivo"; }
      finally { this.targetLoading = false; }
    },
    // setTargetSelectionMode/syncClickSelectionMode/enterClickSelection: la
    // sección "Filtro por capa" ofrece dos formas EXCLUYENTES de acotar la
    // capa objetivo (ver template) — por atributos (AdvancedAttributeGroup,
    // sin cambios) o clickeando entidades directamente en el mapa
    // (targetFeatureSelectionService.js, mismo patrón que advancedRoiService
    // ya usa para la región de interés). syncClickSelectionMode arma/pausa el
    // listener de clic según el modo activo y si la capa objetivo está
    // encendida en el mapa — nunca borra la selección ya hecha, sólo
    // cambiar de capa objetivo o "Limpiar selección" la descartan.
    setTargetSelectionMode(mode) {
      if (this.targetSelectionMode === mode) return;
      const next = this.cloneDraft();
      next.crossLayer.targetSelectionMode = mode;
      this.emitDraft(next);
    },
    // Ícono junto a "Filtro por capa" — "por atributos" es el default cuando
    // no está apretado (mismo ícono crosshairs que MapControlsCustom.vue ya
    // usa para geolocalizar/"apuntar" un punto en el mapa).
    toggleTargetSelectionMode() {
      this.setTargetSelectionMode(this.targetSelectionMode === "click" ? "attribute" : "click");
    },
    syncClickSelectionMode() {
      if (this.draft.crossLayer.enabled && this.targetSelectionMode === "click" && this.targetLayerActive) {
        this.enterClickSelection();
      } else {
        targetFeatureSelectionService.stop();
      }
    },
    async enterClickSelection() {
      const targetLayerId = this.draft.crossLayer.targetLayerId;
      const targetLayer = attributesTableService.findKnownLayer(targetLayerId);
      if (!targetLayer) return;
      const isStale = () => this.targetSelectionMode !== "click" || String(this.draft.crossLayer.targetLayerId) !== String(targetLayerId);
      const metadata = await attributesTableService.getLayerMetadata(targetLayer);
      if (isStale()) return;
      const initialFeatures = await resolveInitialSelectionFeatures(targetLayer, metadata, this.targetSelectionIds);
      if (isStale()) return;
      targetFeatureSelectionService.start(targetLayer, initialFeatures, ids => this.setTargetSelectionIds(ids));
    },
    setTargetSelectionIds(ids) {
      const next = this.cloneDraft();
      next.crossLayer.targetSelection = { ids };
      this.emitDraft(next);
    },
    removeSelectedFeature(id) { targetFeatureSelectionService.removeId(id); },
    clearMapSelection() {
      targetFeatureSelectionService.clear();
      this.setTargetSelectionIds([]);
      this.syncClickSelectionMode();
    },
    toggleSection(section, enabled) { const next = this.cloneDraft(); next[section].enabled = enabled; this.emitDraft(next); },
    setRoi(spec) { this.drawingKind = null; const next = this.cloneDraft(); next.roi.spec = cloneFilter(spec); this.emitDraft(next); },
    changeOperation(operation) {
      if (!this.roiSpec) return;
      const spec = cloneFilter(this.roiSpec); spec.operation = operation; this.setRoi(spec);
    },
    useViewport() { const spec = advancedRoiService.viewportSpec(this.roiOperation); if (spec) this.setRoi(spec); },
    startDrawing(kind) {
      const tools = this.$store.getters.getMapTools;
      if (tools && tools.draw && tools.draw.active) {
        if (!window.confirm("La herramienta Dibujar está activa. ¿Desea desactivarla para crear la región de interés?")) return;
        this.$store.dispatch("setMapToolSelection", { tool: "draw", active: false });
      }
      this.drawingKind = kind;
      advancedRoiService.startDrawing(kind, this.roiOperation);
    },
    updateBbox(index, value) { const spec = cloneFilter(this.roiSpec); spec.shape.bbox[index] = Number(value); this.setRoi(spec); },
    updateRectCenter(axis, value) {
      const spec = cloneFilter(this.roiSpec); const current = this.rectCenter; const delta = Number(value) - current[axis];
      spec.shape.bbox[axis] += delta; spec.shape.bbox[axis + 2] += delta; this.setRoi(spec);
    },
    updateRectDimension(axis, value) {
      const requested = Number(value); const current = this.rectDimensions[axis]; if (!(requested > 0) || !(current > 0)) return;
      const spec = cloneFilter(this.roiSpec); const center = this.rectCenter;
      const span = (spec.shape.bbox[axis + 2] - spec.shape.bbox[axis]) * requested / current;
      spec.shape.bbox[axis] = center[axis] - span / 2; spec.shape.bbox[axis + 2] = center[axis] + span / 2; this.setRoi(spec);
    },
    updateCircleCenter(axis, value) { const spec = cloneFilter(this.roiSpec); spec.shape.center[axis] = Number(value); this.setRoi(spec); },
    updateRadius(value) { const spec = cloneFilter(this.roiSpec); spec.shape.radiusMeters = Number(value); this.setRoi(spec); },
    updateVertex(index, axis, value) { const spec = cloneFilter(this.roiSpec); spec.shape.vertices[index][axis] = Number(value); this.setRoi(spec); },
    validateRoi(spec) {
      const errors = []; const shape = spec.shape;
      const validPoint = point => point.every(Number.isFinite) && point[0] >= -180 && point[0] <= 180 && point[1] >= -85.0511 && point[1] <= 85.0511;
      if (shape.kind === "circle") {
        if (!validPoint(shape.center)) errors.push("El centro del círculo está fuera del rango válido");
        if (!(Number(shape.radiusMeters) > 0)) errors.push("El radio debe ser mayor que cero");
      } else if (["rectangle", "viewport"].includes(shape.kind)) {
        const bbox = shape.bbox.map(Number);
        if (!validPoint([bbox[0], bbox[1]]) || !validPoint([bbox[2], bbox[3]]) || bbox[0] >= bbox[2] || bbox[1] >= bbox[3]) errors.push("Los límites del rectángulo no son válidos");
      } else {
        const vertices = shape.vertices || [];
        if (vertices.length < 3 || vertices.some(point => !validPoint(point))) errors.push("El polígono necesita al menos tres vértices válidos");
        else {
          try { if (turf.kinks(turf.polygon(roiPolygon4326(spec))).features.length) errors.push("El polígono no puede cruzarse consigo mismo"); }
          catch (error) { errors.push("La geometría del polígono no es válida"); }
        }
      }
      return errors;
    },
    distance(a, b) { return turf.distance(turf.point(a), turf.point(b), { units: "kilometers" }) * 1000; },
    formatNumber(value, decimals = 6) { return Number.isFinite(Number(value)) ? Number(value).toFixed(decimals) : ""; },
    formatMetric(value, unit) {
      if (unit === "m²" && value > 1000000) return `${(value / 1000000).toFixed(2)} km²`;
      if (unit === "m" && value > 1000) return `${(value / 1000).toFixed(2)} km`;
      return `${value.toFixed(2)} ${unit}`;
    },
    closePanel() { advancedRoiService.stopInteractions(); targetFeatureSelectionService.stop(); this.$emit("close"); },
    startDrag(event) {
      if (window.innerWidth <= 600) return;
      this.dragging = true; this.dragStart = { clientX: event.clientX, clientY: event.clientY, x: this.x, y: this.y };
      window.addEventListener("pointermove", this.onDrag); window.addEventListener("pointerup", this.stopDrag);
    },
    onDrag(event) {
      if (!this.dragging) return;
      const width = this.$el ? this.$el.offsetWidth : 520;
      this.x = Math.max(0, Math.min(window.innerWidth - width, this.dragStart.x + event.clientX - this.dragStart.clientX));
      this.y = Math.max(0, Math.min(window.innerHeight - this.bottomOffset - 44, this.dragStart.y + event.clientY - this.dragStart.clientY));
    },
    stopDrag() { this.dragging = false; window.removeEventListener("pointermove", this.onDrag); window.removeEventListener("pointerup", this.stopDrag); },
  },
};
</script>

<style scoped>
.advanced-panel { position: fixed; width: 520px; display: flex; flex-direction: column; overflow: hidden; background: #fff; border: 1px solid var(--color-line); border-radius: 5px; box-shadow: 0 8px 28px rgba(15, 23, 42, .28); z-index: 1000; }
.advanced-panel.minimized { max-height: none !important; }
.advanced-header { flex: 0 0 auto; display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: var(--color-navy); color: #fff; cursor: move; touch-action: none; }
.advanced-header button { width: 30px; height: 28px; margin-left: 5px; border: 0; border-radius: 4px; background: rgba(255,255,255,.16); color: #fff; cursor: pointer; font-size: 1.1rem; }
.advanced-body { overflow: auto; padding: 12px; }
.advanced-section + .advanced-section { margin-top: 13px; padding-top: 13px; border-top: 1px solid var(--color-line); }
.section-title { margin-bottom: 9px; color: var(--color-navy); font-size: .86rem; font-weight: 700; }
.section-title label { display:flex; align-items:center; gap:8px; }
.section-title input[type="checkbox"] { appearance:auto; width:16px !important; height:16px !important; min-width:16px; min-height:16px; margin:0; padding:0; box-shadow:none; accent-color:var(--color-orange); }
.control-label { display: flex; flex-direction: column; gap: 5px; font-size: .76rem; font-weight: 600; }
.control-label select, .numeric-editor input { min-height: 34px; box-sizing: border-box; padding: 5px 8px; border: 1px solid #cbd5e1; border-radius: 4px; background: #fff; font-size:.8rem; }
.cross-operation { margin-top: 9px; }
.cross-roi-toggle { display:flex; align-items:center; gap:7px; margin-top:9px; font-size:.75rem; }
.cross-roi-toggle input { appearance:auto; width:15px !important; height:15px !important; margin:0; }
.cross-status { margin:9px 0 0; color:#64748b; font-size:.72rem; }
.cross-status.error { color:#b91c1c; }
.target-attribute-filter { margin-top:10px; padding:9px; border-radius:4px; background:#f8fafc; }
.target-attribute-filter > p { margin:0 0 8px; color:#475569; font-size:.72rem; font-weight:600; }
.section-title.cross-layer-title { display:flex; align-items:center; justify-content:space-between; }
.click-select-icon-btn { display:inline-flex; align-items:center; justify-content:center; width:28px; height:28px; padding:0; border:1px solid #cbd5e1; border-radius:4px; background:#fff; color:#64748b; cursor:pointer; font-size:.9rem; }
.click-select-icon-btn.active { border-color:var(--color-orange); background:var(--color-orange); color:#fff; }
.click-select-icon-btn:disabled { opacity:.4; cursor:not-allowed; }
.target-click-selection { margin-top:10px; padding:9px; border-radius:4px; background:#f8fafc; }
.selection-count { margin:8px 0 0; color:#334155; font-size:.78rem; font-weight:600; }
.selection-chips { display:flex; flex-wrap:wrap; gap:5px; margin:8px 0 0; padding:0; list-style:none; }
.selection-chips .chip { display:inline-flex; align-items:center; gap:5px; padding:3px 6px; border-radius:10px; background:#e2e8f0; color:#334155; font-size:.72rem; }
.selection-chips .chip button { display:inline-flex; border:0; padding:0; background:transparent; color:#64748b; cursor:pointer; font-size:.85rem; line-height:1; }
.target-click-selection .small { margin-top:9px; }
.shape-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; margin-top: 8px; }
.shape-grid button { min-height: 34px; padding: 6px 8px; border: 1px solid var(--color-line); border-radius: 4px; background: #fff; cursor: pointer; font-size: .8rem; }
.shape-grid button.active { background: var(--color-orange); border-color: var(--color-orange); color: #fff; font-weight: 600; }
.geometry-summary { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-top:10px; padding:8px 9px; border-radius:4px; background:#f8fafc; }
.geometry-summary .shape-caption { margin:0; }
.edit-coordinates { display:inline-flex; align-items:center; gap:6px; min-height:32px; padding:5px 9px; border:1px solid #cbd5e1; border-radius:4px; background:#fff; color:#334155; cursor:pointer; font-size:.78rem; }
.edit-coordinates.active { border-color:var(--color-orange); color:var(--color-orange); }
.numeric-editor { margin-top: 10px; padding: 9px; border-radius: 4px; background: #f8fafc; }
.shape-caption { margin: 0 0 8px; font-size: .78rem; font-weight: 700; }
.numeric-grid { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 7px; }
.numeric-grid.four { grid-template-columns: repeat(4, minmax(0,1fr)); margin-bottom: 7px; }
.numeric-grid label { min-width: 0; font-size: .67rem; color: #475569; }
.numeric-grid input { width: 100%; margin-top: 3px; }
.vertex-row { display: grid; grid-template-columns: 28px 1fr 1fr; gap: 5px; margin-bottom: 5px; align-items: center; }
.vertex-row input { width: 100%; }
.vertices-editor p { margin: 7px 0 0; color: #64748b; font-size: .7rem; }
.validation-errors { margin-top: 10px; padding: 8px; border-left: 3px solid #dc2626; background: #fef2f2; color: #b91c1c; font-size: .72rem; }
.validation-errors p { margin: 2px 0; }
.advanced-footer { flex: 0 0 auto; display: flex; justify-content: flex-end; gap: 8px; padding: 10px 12px; border-top: 1px solid var(--color-line); background: #f8fafc; }
.advanced-footer button { min-height: 34px; padding: 7px 14px; border-radius: 4px; cursor: pointer; font-weight: 600; }
.advanced-footer .secondary { border: 1px solid #cbd5e1; background: #fff; }
.advanced-footer .primary { border: 1px solid var(--color-orange); background: var(--color-orange); color: #fff; }
.advanced-footer button:disabled { opacity: .5; cursor: not-allowed; }
@media (max-width: 600px) { .advanced-panel { left: 8px !important; top: 8px !important; width: calc(100vw - 16px); } .advanced-header { cursor: default; } .shape-grid, .numeric-grid.four { grid-template-columns: repeat(2, 1fr); } .geometry-summary{align-items:flex-start;flex-direction:column}.edit-coordinates{width:100%;justify-content:center} }
</style>
